// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { Button } from "@/components/ui/button";
import { SaveActionButtons } from "@/components/shared/SaveActionButtons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Save,
  Loader2,
  Truck,
  Package,
  Trash2,
  Plus,
  ArrowDownToLine,
  MapPin,
  User,
  Phone,
  Info,
  RefreshCw,
  PackageCheck,
  Lock,
  AlertCircle,
  Receipt,
  Layers,
  Sparkles,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FQCReadySelectModal from "./components/FQCReadySelectModal";
import CombineOrderModal from "./components/CombineOrderModal";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import {
  useOrderLookup,
  useCustomerLookup,
  useProductLookup,
} from "@/hooks/useFKLookup";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { F } from "@/components/shared/F";
import { focusFirstError } from "@/lib/form/formUtils";
import { formatNumber, formatDate, formatCurrency } from "@/lib/utils";
import { fqcDeliveryGroupError, fqcStageOf } from "@/lib/fqc-stage";
import { numberToVietnameseWords } from "@/lib/numberToWords";

import { toVnDateString } from "@apv-erp/status";
export interface DeliveryItemRow {
  orderDetailId?: string;
  productId: string;
  productName: string;
  poNumber?: string;
  soNumber?: string;
  unit: string;
  quantity: number; // SL đặt hàng
  deliveredQty: number; // SL thực giao đợt này
  lossQty: number; // SL bù hao
  countedQty: number; // SL thực tính
  unitPrice: number; // Giá net (chưa bao gồm VAT)
  amount: number;
  notes: string;
  isServiceItem?: boolean;
  serviceType?: string;
  _orderQty?: number; // SL đặt gốc
  _alreadyDelivered?: number; // Đã giao các đợt trước
  _qcPassedQty?: number; // KCS FQC Đạt
  _fgStockQty?: number; // Tồn kho WMS
  _remainingQty?: number; // Còn thiếu = max(0, orderQty - alreadyDelivered)
  _suggestedQty?: number; // Gợi ý giao = min(remainingQty, max(qcPassedQty, fgStockQty))
  _totalIssued?: number; // Số lượng xuất kho
  _maxDeliverable?: number;
  _fgQty?: number;
  productImage?: string | null;
  printLayoutUrl?: string | null;
}

export const extractVatRate = (name?: string): number => {
  if (!name) return 8;
  const match = name.match(/(\d+)%/);
  return match ? Number(match[1]) : 8;
};

export function canUpdateDeliveryLocation(status?: string | null): boolean {
  return status === "DRAFT" || status === "SCHEDULED";
}

export function canEditDeliveryDocument(status?: string | null): boolean {
  return (
    status !== "DELIVERED" &&
    status !== "COMPLETED" &&
    status !== "CANCELLED"
  );
}

export const applyVatSync = (
  currentItems: DeliveryItemRow[],
  enabled: boolean,
  rate: number,
): DeliveryItemRow[] => {
  // Lọc riêng các dòng không phải VAT
  const nonVatItems = currentItems.filter(
    (it) => !(it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat"))))
  );

  if (!enabled) {
    return nonVatItems;
  }

  const goodsSubtotal = nonVatItems.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const vatAmount = Math.round((goodsSubtotal * rate) / 100);

  const vatRow: DeliveryItemRow = {
    productId: "",
    productName: `Thuế Giá Trị Gia Tăng (VAT ${rate}%)`,
    unit: "Gói",
    quantity: 1,
    deliveredQty: 1,
    lossQty: 0,
    countedQty: 1,
    unitPrice: vatAmount,
    amount: vatAmount,
    notes: `Dịch vụ VAT ${rate}% tính trên tiền hàng (${formatNumber(goodsSubtotal)} đ)`,
    isServiceItem: true,
    serviceType: "VAT",
  };

  return [...nonVatItems, vatRow];
};

export const SERVICE_TYPES = [
  { value: "SHIPPING", label: "Vận chuyển & Bốc xếp", defaultName: "Phí vận chuyển & bốc dỡ" },
  { value: "VAT", label: "Thuế Giá Trị Gia Tăng (VAT)", defaultName: "Thuế GTGT (VAT)" },
  { value: "DESIGN", label: "Chế bản & Thiết kế phụ trội", defaultName: "Phí chế bản & thiết kế phụ trội" },
  { value: "PACKAGING", label: "Đóng gói Pallet / Thùng đặc biệt", defaultName: "Phí đóng gói kiện pallet đặc biệt" },
  { value: "OTHER", label: "Phụ phí dịch vụ khác", defaultName: "Phụ phí dịch vụ ngoài đơn" },
];

export interface DeliveryFormData {
  deliveryId: string;
  customerId: string;
  orderId: string;
  salesUserId?: string | null;
  deliveryDate: string;
  deliveryType: string;
  deliveryFee: number;
  address: string;
  transshipmentAddr: string;
  receiverName: string;
  receiverPhone: string;
  status: string;
  driverName: string;
  vehicleNumber: string;
  notes: string;
  items: DeliveryItemRow[];
  totalAmount: number;
}

interface DeliveryFormProps {
  defaultValues?: Partial<DeliveryFormData>;
  filterDetailIds?: string[];
  fqcBatchIds?: string[];
  focusSection?: "location";
  onSubmit: (data: DeliveryFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export default function DeliveryForm({
  defaultValues,
  filterDetailIds,
  fqcBatchIds,
  focusSection,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu",
}: DeliveryFormProps) {
  const canEditLocation =
    !defaultValues?.deliveryId ||
    !defaultValues?.status ||
    canUpdateDeliveryLocation(defaultValues.status);
  const [highlightLocation, setHighlightLocation] = useState(
    focusSection === "location",
  );

  React.useEffect(() => {
    if (focusSection !== "location") return;
    setHighlightLocation(true);
    const timer = window.setTimeout(() => {
      const sectionEl = document.getElementById("delivery-location-section");
      sectionEl?.scrollIntoView({ behavior: "smooth", block: "center" });
      const addrInput = document.getElementById("address") as HTMLInputElement | null;
      if (addrInput && !addrInput.disabled) {
        addrInput.focus();
      }
    }, 80);
    const clearTimer = window.setTimeout(() => setHighlightLocation(false), 3200);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clearTimer);
    };
  }, [focusSection]);
  const initialVatItem = (defaultValues as any)?.deliveryItems?.find(
    (it: any) => it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat")))
  ) || defaultValues?.items?.find(
    (it: any) => it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat")))
  );

  const [vatEnabled, setVatEnabled] = useState<boolean>(() => {
    if (initialVatItem) return true;
    if (!defaultValues?.deliveryId) return true; // Mặc định bật VAT 8% khi tạo mới phiếu giao hàng
    return false;
  });

  const [vatRate, setVatRate] = useState<number>(() => {
    return initialVatItem ? extractVatRate(initialVatItem.productName) : 8;
  });

  const [items, setItems] = useState<DeliveryItemRow[]>(() => {
    const sourceItems =
      (defaultValues as any)?.deliveryItems || defaultValues?.items || [];
    const mapped = sourceItems.map((it: any) => ({
      orderDetailId: it.orderDetailId || undefined,
      productId: it.productId || "",
      productName: it.productName || it.product?.productName || it.product?.name || "—",
      poNumber: it.poNumber || "",
      soNumber: it.soNumber || "",
      unit: it.unit || it.product?.unit || (it.isServiceItem ? "Gói" : "Hộp"),
      quantity: Number(it.quantity) || 0,
      deliveredQty: Number(it.deliveredQty) || 0,
      lossQty: Number(it.lossQty) || 0,
      countedQty: Number(it.countedQty) || 0,
      unitPrice: Number(it.unitPrice) || 0,
      amount: Number(it.amount) || 0,
      notes: it.notes || "",
      isServiceItem: Boolean(it.isServiceItem || false),
      serviceType: it.serviceType || undefined,
      _orderQty: Number(it.quantity) || 0,
      _alreadyDelivered: Number(it.alreadyDelivered || it.qtyDelivered || 0),
      _qcPassedQty: Number(it.qcPassedQty || 0),
      _fgStockQty: Number(it.fgStockQty || it._fgQty || 0),
      _remainingQty: Number(it.remainingQty || 0),
      _suggestedQty: Number(it.suggestedQty || 0),
      productImage: it.productImage || it.product?.image || null,
      printLayoutUrl: it.printLayoutUrl || it.printLayout?.fileUrl || null,
    }));
    return mapped;
  });
  const itemsRef = useRef(items);
  React.useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [showFqcModal, setShowFqcModal] = useState(false);
  const [showCombineModal, setShowCombineModal] = useState<boolean>(false);

  const handleCombineOrder = (order: any, selectedDetailIds: string[]) => {
    if (!order) return;
    const details = order.orderDetails || order.items || [];
    const chosenDetails = details.filter((d: any) => selectedDetailIds.includes(d.id));

    const getNumber = (val: any) => {
      if (val && typeof val === "object" && val.toString)
        return parseFloat(val.toString());
      return Number(val) || 0;
    };

    const newRows: DeliveryItemRow[] = chosenDetails.map((d: any) => {
      const orderQty = Number(d.quantity) || 0;
      const alreadyDelivered = Number(d.alreadyDelivered ?? d.qtyDelivered) || 0;
      const totalIssued = Number(d.totalIssued) || 0;
      const qcPassedQty = Number(d.qcPassedQty) || 0;
      const fgStockQty = Number(d.fgStockQty) || 0;
      const remainingQty = d.remainingQty !== undefined
        ? Number(d.remainingQty)
        : Math.max(0, orderQty - alreadyDelivered);
      const suggestedQty = d.suggestedQty !== undefined
        ? Number(d.suggestedQty)
        : (Math.max(qcPassedQty, fgStockQty) > 0
            ? Math.min(remainingQty, Math.max(qcPassedQty, fgStockQty))
            : remainingQty);

      const defaultDeliveredQty = suggestedQty > 0
        ? suggestedQty
        : (remainingQty > 0 ? remainingQty : orderQty);

      const price = getNumber(d.unitPrice) || getNumber(d.priceVND) || 0;

      return {
        orderDetailId: d.id,
        productId: d.productId,
        productName: d.productName || d.product?.productName || d.product?.name || "—",
        poNumber: d.poReference || order.poNumber || "",
        soNumber: d.soReference || order.orderId || order.soNumber || "",
        unit: d.unit || d.product?.unit || "Hộp",
        quantity: orderQty,
        deliveredQty: defaultDeliveredQty,
        lossQty: 0,
        countedQty: defaultDeliveredQty,
        unitPrice: price,
        amount: defaultDeliveredQty * price,
        notes: `Gộp từ ${order.orderId}`,
        _orderQty: orderQty,
        _alreadyDelivered: alreadyDelivered,
        _qcPassedQty: qcPassedQty,
        _fgStockQty: fgStockQty,
        _remainingQty: remainingQty,
        _suggestedQty: suggestedQty,
        _totalIssued: totalIssued,
        _maxDeliverable: remainingQty > 0 ? remainingQty : orderQty,
        _fgQty: fgStockQty,
        productImage: d.product?.image || null,
        printLayoutUrl: d.printLayout?.fileUrl || null,
      } as any;
    });

    if (newRows.length > 0) {
      const nonVatItems = items.filter(
        (it) => !(it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat"))))
      );
      const combined = [...nonVatItems, ...newRows];
      setItems(applyVatSync(combined, vatEnabled, vatRate));
      toast.success(`Đã gộp ${newRows.length} mặt hàng từ đơn ${order.orderId}`);
    }
  };

  const handleToggleVat = (enabled: boolean) => {
    setVatEnabled(enabled);
    setItems((prev) => applyVatSync(prev, enabled, vatRate));
  };

  const handleChangeVatRate = (rate: number) => {
    setVatRate(rate);
    if (vatEnabled) {
      setItems((prev) => applyVatSync(prev, true, rate));
    }
  };

  const handleSelectFqcBatch = (batch: any) => {
    if (batch.customerId) {
      form.setFieldValue("customerId", batch.customerId);
    }
    if (batch.soId) {
      form.setFieldValue("orderId", batch.soId);
    }
    if (batch.deliveryAddress) {
      form.setFieldValue("address", batch.deliveryAddress);
    }
    const currentNotes = form.getFieldValue("notes") || "";
    const appendNote = `Xuất giao từ ${batch.outsourcePoId ? "đơn gia công" : "LSX"} ${batch.poCode || ""} (KCS FQC Đạt)`;
    form.setFieldValue("notes", currentNotes ? `${currentNotes}; ${appendNote}` : appendNote);

    const price = Number(batch.unitPrice) || 0;
    const qty = Number(batch.qtyRemaining) || Number(batch.totalQty) || 0;
    const orderQty = Number(batch.orderQty || batch.totalQty) || qty;
    const alreadyDelivered = Number(batch.alreadyDelivered || batch.deliveredQty) || 0;
    const qcPassedQty = Number(batch.qcPassedQty || batch.qtyPassed) || qty;
    const fgStockQty = Number(batch.fgStockQty) || 0;
    const remainingQty = Number(batch.remainingQty || batch.qtyRemaining) || Math.max(0, orderQty - alreadyDelivered);
    const suggestedQty = Number(batch.suggestedQty) || (Math.max(qcPassedQty, fgStockQty) > 0 ? Math.min(remainingQty, Math.max(qcPassedQty, fgStockQty)) : remainingQty);
    const deliverQty = suggestedQty > 0 ? suggestedQty : (remainingQty > 0 ? remainingQty : qty);

    const item: DeliveryItemRow = {
      productId: batch.productId,
      // Dòng đơn hàng của lô: giao lô gia công 100% tính đúng SL đã giao của dòng
      orderDetailId: batch.orderDetailId || undefined,
      productName: batch.productName || "Sản phẩm",
      poNumber: batch.poCode || "",
      soNumber: batch.soCode || "",
      unit: batch.unit || "Hộp",
      quantity: orderQty,
      deliveredQty: deliverQty,
      lossQty: 0,
      countedQty: deliverQty,
      unitPrice: price,
      amount: deliverQty * price,
      notes: `Từ KCS FQC Đạt (LSX: ${batch.poCode})`,
      _orderQty: orderQty,
      _alreadyDelivered: alreadyDelivered,
      _qcPassedQty: qcPassedQty,
      _fgStockQty: fgStockQty,
      _remainingQty: remainingQty,
      _suggestedQty: suggestedQty,
      _maxDeliverable: remainingQty > 0 ? remainingQty : orderQty,
      _fgQty: fgStockQty,
      productImage: batch.productImage || null,
      printLayoutUrl: batch.layoutUrl || null,
    };
    setItems(applyVatSync([item], vatEnabled, vatRate));
    toast.success(`Đã nạp dữ liệu từ Lô FQC Đạt của LSX ${batch.poCode}!`);
  };

  // Tự động gom lô xuất giao khi nhận fqcBatchIds từ URL / luồng KCS
  useEffect(() => {
    if (!fqcBatchIds || fqcBatchIds.length === 0) return;
    let isMounted = true;
    (async () => {
      try {
        const batches = await Promise.all(
          fqcBatchIds.map((id) => (trpcClient as any).qcFinal.getById.query({ id }))
        );
        if (!isMounted) return;
        // Một phiếu giao chỉ cho một khách hàng; lô đã lên phiếu giao đủ thì bỏ qua.
        // Lô gia công 100% không có lệnh SX: dựng thông tin dạng lệnh từ chính biên bản FQC (đơn gia công + dòng đơn)
        const loaded = batches.filter(Boolean).map((b: any) =>
          b.productionOrder || !b.outsourcePo
            ? b
            : {
                ...b,
                productionOrder: {
                  orderId: b.outsourcePo.poId,
                  customerId: b.customerId || b.customer?.id,
                  customer: b.customer,
                  salesOrder: b.order,
                  orderDetail: b.orderDetail,
                  product: b.product,
                  productName: b.productName || b.product?.productName,
                  qtyPlan: b.orderDetail?.quantity,
                  productionOrderSources: [],
                },
              },
        );
        const groupError = fqcDeliveryGroupError(loaded);
        if (groupError) {
          toast.error(groupError);
          return;
        }
        // Chỉ lô đã đóng gói & nhập kho, chưa lên phiếu giao đủ
        const valid = loaded.filter((b: any) => {
          const stage = fqcStageOf(b);
          return stage === "WAITING_DELIVERY" || stage === "READY_FOR_DISPATCH";
        });
        if (valid.length < loaded.length) {
          toast.warning(`Bỏ qua ${loaded.length - valid.length} lô chưa đóng gói & nhập kho hoặc đã lên phiếu giao.`);
        }
        if (valid.length > 0) {
          const first = valid[0];
          const po = first.productionOrder;
          if (po?.customerId) {
            form.setFieldValue("customerId", po.customerId);
          }
          // Lệnh không nối trực tiếp đơn hàng thì lấy đơn hàng từ nguồn của lệnh (FB-KLDFEF)
          const firstSourceOrder = po?.productionOrderSources?.[0]?.order;
          if (po?.salesOrder?.id || firstSourceOrder?.id || po?.orderId) {
            form.setFieldValue("orderId", po?.salesOrder?.id || firstSourceOrder?.id || po?.orderId);
          }
          const salesUserId = po?.salesOrder?.salesUserId || firstSourceOrder?.salesUserId;
          if (salesUserId && !form.getFieldValue("salesUserId")) {
            form.setFieldValue("salesUserId", salesUserId);
          }
          if (po?.salesOrder?.deliveryAddress || po?.customer?.address) {
            form.setFieldValue("address", po?.salesOrder?.deliveryAddress || po?.customer?.address || "");
          }
          if (po?.customer?.phone) {
            form.setFieldValue("receiverPhone", po.customer.phone);
          }
          if (po?.customer?.name) {
            form.setFieldValue("receiverName", po.customer.name);
          }
          const notes = `Gom lô xuất giao từ ${valid.length} đợt KCS: ` + valid.map((b: any) => b.productionOrder?.orderId || b.id).join(", ");
          form.setFieldValue("notes", notes);

          const newItems: DeliveryItemRow[] = valid.flatMap((b: any) => {
            const bpo = b.productionOrder;
            const bprod = bpo?.product;
            const orderQty = Number(bpo?.qtyPlan || bpo?.actualQuantity) || 0;
            const alreadyDelivered = Number(b.deliveryInfo?.deliveredQty) || 0;
            // SL giao được của lô = SL đạt đóng gói đã nhập kho (phiếu đóng gói), không phải SL Đạt KCS
            const qcPassedQty = Number(b.packingSlip?.qtyPacked ?? b.qtyPassed ?? bpo?.actualQuantity) || 0;
            const fgStockQty = Number(b.fgStockQty) || 0;
            const remainingQty = Math.max(0, orderQty - alreadyDelivered);
            const suggestedQty = Math.max(qcPassedQty, fgStockQty) > 0 ? Math.min(remainingQty, Math.max(qcPassedQty, fgStockQty)) : remainingQty;
            const deliverQty = suggestedQty > 0 ? suggestedQty : (remainingQty > 0 ? remainingQty : qcPassedQty);
            // Đơn giá theo dòng đơn hàng của lệnh (như hàng đợi xuất giao), chỉ khi không có mới lấy giá sản phẩm (FB-KLDFEF).
            // Dòng đơn hàng: liên kết trực tiếp, không có thì lấy từ nguồn của lệnh.
            const sources: any[] = bpo?.productionOrderSources || [];
            const od = bpo?.orderDetail || (sources.length === 1 ? sources[0].orderDetail : null);
            const price = od ? Number(od.priceVND || od.unitPrice || 0) : Number(bprod?.price) || 0;

            // Lệnh ghép nhiều dòng đơn hàng: mỗi dòng đơn hàng một dòng giao, đúng đơn giá, chia số giao theo phần còn lại của từng dòng
            if (!bpo?.orderDetail && sources.length > 1) {
              let pool = deliverQty;
              return sources.map((src: any) => {
                const line = src.orderDetail || {};
                const lineQty = Number(src.quantity || line.quantity) || 0;
                const lineRemaining = Math.max(0, lineQty - (Number(src.deliveredQty) || 0));
                const qty = Math.min(pool, lineRemaining);
                pool -= qty;
                const linePrice = Number(line.priceVND || line.unitPrice || 0);
                return {
                  productId: bprod?.id || "",
                  orderDetailId: src.orderDetailId || line.id || undefined,
                  productName: line.productName || bprod?.productName || bpo?.productName || "Sản phẩm",
                  poNumber: bpo?.orderId || "",
                  soNumber: src.order?.orderId || "",
                  unit: bprod?.unit || "Hộp",
                  quantity: lineQty,
                  deliveredQty: qty,
                  lossQty: 0,
                  countedQty: qty,
                  unitPrice: linePrice,
                  amount: qty * linePrice,
                  notes: `KCS Lô Đạt (${bpo?.orderId || b.id})`,
                  _orderQty: lineQty,
                  _alreadyDelivered: Number(src.deliveredQty) || 0,
                  _qcPassedQty: qcPassedQty,
                  _fgStockQty: fgStockQty,
                  _remainingQty: lineRemaining,
                  _suggestedQty: qty,
                  _maxDeliverable: lineRemaining > 0 ? lineRemaining : lineQty,
                  productImage: bprod?.image || null,
                  printLayoutUrl: bpo?.printLayouts?.[0]?.fileUrl || null,
                };
              });
            }

            return [{
              orderDetailId: od?.id || undefined,
              productId: bprod?.id || "",
              productName: bprod?.productName || bpo?.productName || "Sản phẩm",
              poNumber: bpo?.orderId || "",
              soNumber: bpo?.salesOrder?.orderId || "",
              unit: bprod?.unit || "Hộp",
              quantity: orderQty,
              deliveredQty: deliverQty,
              lossQty: 0,
              countedQty: deliverQty,
              unitPrice: price,
              amount: deliverQty * price,
              notes: `KCS Lô Đạt (${bpo?.orderId || b.id})`,
              _orderQty: orderQty,
              _alreadyDelivered: alreadyDelivered,
              _qcPassedQty: qcPassedQty,
              _fgStockQty: fgStockQty,
              _remainingQty: remainingQty,
              _suggestedQty: suggestedQty,
              _maxDeliverable: remainingQty > 0 ? remainingQty : orderQty,
              productImage: bprod?.image || null,
              printLayoutUrl: bpo?.printLayouts?.[0]?.fileUrl || null,
            }];
          });
          setItems(applyVatSync(newItems, vatEnabled, vatRate));
          toast.success(`Đã tự động nạp ${valid.length} lô KCS vào phiếu giao hàng!`);
        }
      } catch (err) {
        console.error("Lỗi nạp danh sách KCS:", err);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [fqcBatchIds]);

  // Tự động nạp chi tiết hàng từ hàng đợi xuất giao khi nhận filterDetailIds từ ReadyToDispatchTab
  useEffect(() => {
    if (!filterDetailIds || filterDetailIds.length === 0 || (fqcBatchIds && fqcBatchIds.length > 0)) return;
    let isMounted = true;
    (async () => {
      try {
        const queueResult = (await trpcClient.deliveries.getReadyToDispatchQueue.query({
          customerId: defaultValues?.customerId || undefined,
        })) as any;
        if (!isMounted) return;
        const matchingBatches = (queueResult?.batches || []).filter((b: any) =>
          filterDetailIds.includes(b.orderDetailId)
        );

        if (matchingBatches.length > 0) {
          const first = matchingBatches[0];
          if (first.customerId && !form.getFieldValue("customerId")) {
            form.setFieldValue("customerId", first.customerId);
          }
          if (first.soId && !form.getFieldValue("orderId")) {
            form.setFieldValue("orderId", first.soId);
          }
          if (first.deliveryAddress && !form.getFieldValue("address")) {
            form.setFieldValue("address", first.deliveryAddress);
          }
          const currentNotes = form.getFieldValue("notes") || "";
          const appendNote = `Xuất giao ${matchingBatches.length} mặt hàng từ hàng đợi KCS & Đóng gói`;
          form.setFieldValue("notes", currentNotes ? `${currentNotes}; ${appendNote}` : appendNote);

          const newItems: DeliveryItemRow[] = matchingBatches.map((b: any) => {
            const price = Number(b.unitPrice) || 0;
            const orderQty = Number(b.orderQty || b.totalQty) || 0;
            const alreadyDelivered = Number(b.alreadyDelivered || b.deliveredQty) || 0;
            const qcPassedQty = Number(b.qcPassedQty) || 0;
            const fgStockQty = Number(b.fgStockQty) || 0;
            const remainingQty = Number(b.remainingQty || b.qtyRemaining) || Math.max(0, orderQty - alreadyDelivered);
            const suggestedQty = Number(b.suggestedQty) || (Math.max(qcPassedQty, fgStockQty) > 0 ? Math.min(remainingQty, Math.max(qcPassedQty, fgStockQty)) : remainingQty);
            const deliverQty = suggestedQty > 0 ? suggestedQty : (remainingQty > 0 ? remainingQty : orderQty);

            return {
              orderDetailId: b.orderDetailId || undefined,
              productId: b.productId || "",
              productName: b.productName || "Sản phẩm",
              poNumber: b.poCode || "",
              soNumber: b.soCode || "",
              unit: b.unit || "Hộp",
              quantity: orderQty,
              deliveredQty: deliverQty,
              lossQty: 0,
              countedQty: deliverQty,
              unitPrice: price,
              amount: deliverQty * price,
              notes: b.poCode ? `Từ LSX: ${b.poCode}` : `Từ đơn hàng: ${b.soCode || ""}`,
              _orderQty: orderQty,
              _alreadyDelivered: alreadyDelivered,
              _qcPassedQty: qcPassedQty,
              _fgStockQty: fgStockQty,
              _remainingQty: remainingQty,
              _suggestedQty: suggestedQty,
              _maxDeliverable: remainingQty > 0 ? remainingQty : orderQty,
              productImage: b.productImage || null,
              printLayoutUrl: b.layoutUrl || null,
            };
          });

          setItems(applyVatSync(newItems, vatEnabled, vatRate));
          toast.success(`Đã tự động nạp ${newItems.length} mặt hàng từ hàng đợi xuất giao!`);
        }
      } catch (err) {
        console.error("Lỗi nạp danh sách chi tiết hàng đợi xuất giao:", err);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [filterDetailIds, defaultValues?.customerId, fqcBatchIds]);

  // Lưu nháp / Lưu (plan-draft-and-save-buttons-v1)
  const currentStatus = (defaultValues as any)?.status as string | undefined;
  const saveModeRef = useRef<"DRAFT" | "SUBMIT">("SUBMIT");

  const form = useForm({
    defaultValues: {
      deliveryId: defaultValues?.deliveryId ?? "",
      customerId: defaultValues?.customerId ?? "",
      orderId: defaultValues?.orderId ?? "",
      salesUserId: defaultValues?.salesUserId ?? "",
      deliveryDate: (function () {
        const d = defaultValues?.deliveryDate;
        if (!d) return toVnDateString();
        const date =
          (d as any) instanceof Date
            ? (d as unknown as Date)
            : new Date(d as string);
        return date && !isNaN(date.getTime())
          ? toVnDateString(date)
          : toVnDateString();
      })(),
      deliveryType: defaultValues?.deliveryType ?? "Giao tới khách",
      deliveryFee: defaultValues?.deliveryFee ?? 0,
      address: defaultValues?.address ?? "",
      transshipmentAddr: defaultValues?.transshipmentAddr ?? "",
      receiverName: defaultValues?.receiverName ?? "",
      receiverPhone: defaultValues?.receiverPhone ?? "",
      status: defaultValues?.status ?? "DRAFT",
      driverName: defaultValues?.driverName ?? "",
      vehicleNumber: defaultValues?.vehicleNumber ?? "",
      notes: defaultValues?.notes ?? "",
    },
    onSubmit: async ({ value }) => {
      const currentItems = itemsRef.current;

      // Validate items
      if (!value.customerId) {
        toast.error("Vui lòng chọn khách hàng");
        const el = document.querySelector('[data-field="customerId"]') || document.getElementById("customerId");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          const focusTarget = (el.matches('button, input, textarea, select, [tabindex]') ? el : el.querySelector('button, input, textarea, select, [tabindex]')) as HTMLElement || el;
          if (focusTarget && typeof focusTarget.focus === "function") {
            focusTarget.focus();
          }
          el.classList.add("ring-2", "ring-destructive", "ring-offset-2", "animate-pulse");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-destructive", "ring-offset-2", "animate-pulse");
          }, 3000);
        }
        return;
      }
      // Lưu nháp được phép chưa có dòng hàng
      if (currentItems.length === 0 && saveModeRef.current !== "DRAFT") {
        toast.error("Vui lòng thêm ít nhất một sản phẩm để giao hàng");
        const el = document.querySelector('[data-field="items"]') || document.getElementById("items");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-destructive", "ring-offset-2", "animate-pulse");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-destructive", "ring-offset-2", "animate-pulse");
          }, 3000);
        }
        return;
      }

      // Prepare safe date
      let isoDate = "";
      try {
        if (value.deliveryDate) {
          const d = new Date(value.deliveryDate);
          if (!isNaN(d.getTime())) isoDate = d.toISOString();
        }
      } catch (e) {
        console.error("Invalid date", e);
      }

      const data: DeliveryFormData = {
        deliveryId: value.deliveryId || (defaultValues?.deliveryId ?? ""),
        customerId: value.customerId || "",
        orderId: value.orderId || "",
        salesUserId: value.salesUserId || null,
        deliveryDate: isoDate,
        deliveryType: value.deliveryType || "Giao tới khách",
        deliveryFee: Number(value.deliveryFee) || 0,
        address: value.address || "",
        transshipmentAddr: value.transshipmentAddr || "",
        receiverName: value.receiverName || "",
        receiverPhone: value.receiverPhone || "",
        status: value.status || "DRAFT",
        driverName: value.driverName || "",
        vehicleNumber: value.vehicleNumber || "",
        notes: value.notes || "",
        items: currentItems,
        // Backend tự tính totalAmount từ items + deliveryFee
      };
      await onSubmit({ ...data, saveMode: saveModeRef.current } as any);
    },
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    saveModeRef.current = "SUBMIT";
    form.handleSubmit();
    requestAnimationFrame(() => {
      focusFirstError(form.state as any);
    });
  };

  // Watch customer ID to filter orders
  const selectedCustomerId = useStore(
    form.store,
    (s: any) => s.values.customerId,
  );

  // Filter customers and orders based on whether we are creating or editing
  const isNew = !defaultValues?.deliveryId;
  const customerLookup = useCustomerLookup(
    isNew ? { hasDeliverableOrders: true } : undefined,
  );

  // For order lookup, restrict to deliverable statuses if creating new
  const orderStatusFilter = isNew
    ? ["APPROVED", "CONFIRMED", "IN_PRODUCTION", "COMPLETED"]
    : undefined;
  const orderLookup = useOrderLookup(
    selectedCustomerId || undefined,
    orderStatusFilter,
    true,
    undefined,
    100,
    isNew ? true : undefined,
  );
  const productLookup = useProductLookup(selectedCustomerId || undefined);

  // Auto-generate delivery code if new
  useEffect(() => {
    if (isNew) {
      trpcClient.deliveries.getNextCode
        .query()
        .then((res) => {
          if (res?.code) {
            form.setFieldValue("deliveryId", res.code);
          }
        })
        .catch(console.error);
    }
  }, [isNew, form]);

  // Auto-load items when orderId is pre-filled (e.g. from OrderView shortcut)
  useEffect(() => {
    if (defaultValues?.customerId && !form.getFieldValue("customerId")) {
      form.setFieldValue("customerId", defaultValues.customerId);
    }
    if (defaultValues?.orderId && !form.getFieldValue("orderId")) {
      form.setFieldValue("orderId", defaultValues.orderId);
    }
    if (defaultValues?.salesUserId && !form.getFieldValue("salesUserId")) {
      form.setFieldValue("salesUserId", defaultValues.salesUserId);
    }
    if (
      defaultValues?.orderId &&
      items.length === 0 &&
      (!filterDetailIds || filterDetailIds.length === 0) &&
      (!fqcBatchIds || fqcBatchIds.length === 0)
    ) {
      loadOrderItems(defaultValues.orderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues?.orderId, defaultValues?.customerId, defaultValues?.salesUserId, filterDetailIds, fqcBatchIds]);

  // ─── Load order items when orderId changes ──────
  const loadOrderItems = async (orderId: string) => {
    if (!orderId) return;
    setLoadingOrder(true);
    try {
      const order = (await trpcClient.orders.getById.query({
        id: orderId,
      })) as any;
      if (!order) {
        toast.error("Không tìm thấy đơn hàng");
        return;
      }

      // Auto-fill customer from order
      if (order.customerId) {
        form.setFieldValue("customerId", order.customerId);
      }
      // Auto-fill salesUser from order or customer
      const resolvedSalesUserId = order.salesUserId || order.customer?.salesUsers?.[0]?.userId;
      if (resolvedSalesUserId && !form.getFieldValue("salesUserId")) {
        form.setFieldValue("salesUserId", resolvedSalesUserId);
      }
      if (order.customer?.address) {
        form.setFieldValue("address", order.customer.address);
      }
      if (order.contactPerson) {
        form.setFieldValue("receiverName", order.contactPerson);
      }
      if (order.phone) {
        form.setFieldValue("receiverPhone", order.phone);
      }

      // Map order details to delivery items
      let details: any[] = order.orderDetails || order.items || [];
      if (filterDetailIds && filterDetailIds.length > 0) {
        details = details.filter(
          (d: any) => filterDetailIds.includes(d.id) || filterDetailIds.includes(d.detailId)
        );
      }

      const hasRemainingItems = details.some((d: any) => (Number(d.quantity) || 0) > (Number(d.qtyDelivered) || 0));

      const newItems: DeliveryItemRow[] = details.reduce(
        (acc: DeliveryItemRow[], d: any) => {
          const orderQty = Number(d.quantity) || 0;
          const alreadyDelivered = Number(d.alreadyDelivered ?? d.qtyDelivered) || 0;
          const totalIssued = Number(d.totalIssued) || 0;
          const qcPassedQty = Number(d.qcPassedQty) || 0;
          const fgStockQty = Number(d.fgStockQty) || 0;
          const remainingQty = d.remainingQty !== undefined
            ? Number(d.remainingQty)
            : Math.max(0, orderQty - alreadyDelivered);
          const suggestedQty = d.suggestedQty !== undefined
            ? Number(d.suggestedQty)
            : (Math.max(qcPassedQty, fgStockQty) > 0
                ? Math.min(remainingQty, Math.max(qcPassedQty, fgStockQty))
                : remainingQty);

          if (hasRemainingItems && remainingQty <= 0) return acc; // Don't show already delivered items if others remain

          // Max allowed to deliver based on Warehouse Issue (if tracked)
          const maxDeliverable = totalIssued > 0 
            ? Math.max(0, totalIssued - alreadyDelivered) 
            : (remainingQty > 0 ? remainingQty : orderQty);

          // Get price from order detail based on frontend or backend structure
          const getNumber = (val: any) => {
            if (val && typeof val === "object" && val.toString)
              return parseFloat(val.toString());
            return Number(val) || 0;
          };
          const price = getNumber(d.unitPrice) || getNumber(d.priceVND) || 0;

          // By default, suggest delivering what can be delivered
          const defaultDeliveredQty = suggestedQty > 0
            ? suggestedQty
            : (remainingQty > 0 ? remainingQty : orderQty);

          acc.push({
            orderDetailId: d.id,
            productId: d.productId,
            productName: d.productName || d.product?.productName || d.product?.name || "—",
            poNumber: d.poReference || order.poNumber || "",
            soNumber: d.soReference || order.soNumber || "",
            unit: d.unit || d.product?.unit || "Hộp",
            quantity: orderQty,
            deliveredQty: defaultDeliveredQty,
            lossQty: 0,
            countedQty: defaultDeliveredQty,
            unitPrice: price,
            amount: defaultDeliveredQty * price,
            notes: "",
            _orderQty: orderQty,
            _alreadyDelivered: alreadyDelivered,
            _qcPassedQty: qcPassedQty,
            _fgStockQty: fgStockQty,
            _remainingQty: remainingQty,
            _suggestedQty: suggestedQty,
            _totalIssued: totalIssued,
            _maxDeliverable: maxDeliverable,
            _fgQty: fgStockQty,
            productImage: d.product?.image || null,
            printLayoutUrl: d.printLayout?.fileUrl || null,
          } as any);
          return acc;
        },
        [],
      );
      setItems(applyVatSync(newItems, vatEnabled, vatRate));
      if (newItems.length > 0) {
      }
    } catch (err: any) {
      console.error("[DeliveryForm] loadOrderItems ERROR:", err);
      toast.error(`Lỗi tải đơn hàng: ${err?.message || "unknown"}`);
    } finally {
      setLoadingOrder(false);
    }
  };

  // ─── Item helpers ──────
  const updateItem = (
    idx: number,
    field: keyof DeliveryItemRow,
    value: any,
  ) => {
    setItems((prev) => {
      const updatedList = prev.map((it, i) => {
        if (i !== idx) return it;
        const updated = { ...it, [field]: value };

        // Recalculate Counted qty and Amount
        if (field === "deliveredQty" || field === "lossQty") {
          updated.countedQty = Math.max(
            0,
            updated.deliveredQty - updated.lossQty,
          );
        }
        if (
          field === "deliveredQty" ||
          field === "lossQty" ||
          field === "unitPrice" ||
          field === "countedQty"
        ) {
          updated.amount = updated.countedQty * updated.unitPrice;
        }
        if (field === "amount") {
          updated.amount = Number(value) || 0;
          if (updated.countedQty > 0) {
            updated.unitPrice = Math.round(updated.amount / updated.countedQty);
          }
        }
        return updated;
      });
      return applyVatSync(updatedList, vatEnabled, vatRate);
    });
  };

  const updateItemMultiple = (
    idx: number,
    updates: Partial<DeliveryItemRow>,
  ) => {
    setItems((prev) => {
      const updatedList = prev.map((it, i) => {
        if (i !== idx) return it;
        let updated = { ...it, ...updates };
        // Recalculate based on current state
        updated.countedQty = Math.max(
          0,
          (updated.deliveredQty || 0) - (updated.lossQty || 0),
        );
        updated.amount = updated.countedQty * (updated.unitPrice || 0);
        return updated;
      });
      return applyVatSync(updatedList, vatEnabled, vatRate);
    });
  };

  const removeItem = (idx: number) => {
    setItems((prev) => {
      const itemToRemove = prev[idx];
      const filtered = prev.filter((_, i) => i !== idx);
      // Nếu người dùng chủ động xóa dòng VAT: tự động gạt tắt Switch VAT
      if (itemToRemove?.isServiceItem && (itemToRemove.serviceType === "VAT" || (itemToRemove.productName && itemToRemove.productName.toLowerCase().includes("vat")))) {
        setVatEnabled(false);
        return filtered;
      }
      return applyVatSync(filtered, vatEnabled, vatRate);
    });
  };

  const addEmptyItem = () => {
    const newItem: DeliveryItemRow = {
      productId: "",
      productName: "",
      unit: "Hộp",
      quantity: 0,
      deliveredQty: 0,
      lossQty: 0,
      countedQty: 0,
      unitPrice: 0,
      amount: 0,
      notes: "",
    };
    setItems((prev) => applyVatSync([...prev, newItem], vatEnabled, vatRate));
  };

  const { options: dbServiceTypes } = useConfigLookup("service_types");
  const effectiveServiceTypes =
    dbServiceTypes && dbServiceTypes.length > 0
      ? dbServiceTypes.map((opt) => {
          const fallback = SERVICE_TYPES.find((s) => s.value === opt.id);
          return {
            value: opt.id,
            label: opt.label,
            defaultName: fallback?.defaultName || opt.label,
          };
        })
      : SERVICE_TYPES;

  const isLocked = defaultValues?.status === "COMPLETED" || defaultValues?.status === "DELIVERED";

  const addServiceItem = (serviceType = "SHIPPING") => {
    if (serviceType === "VAT") {
      handleToggleVat(true);
      return;
    }
    const matched =
      effectiveServiceTypes.find((s) => s.value === serviceType) ||
      SERVICE_TYPES.find((s) => s.value === serviceType) ||
      effectiveServiceTypes[0] ||
      SERVICE_TYPES[0];
    const newItem: DeliveryItemRow = {
      productId: "",
      productName: matched.defaultName,
      unit: "Gói",
      quantity: 1,
      deliveredQty: 1,
      lossQty: 0,
      countedQty: 1,
      unitPrice: 0,
      amount: 0,
      notes: `Phụ phí dịch vụ (${matched.label})`,
      isServiceItem: true,
      serviceType: matched.value,
    };
    setItems((prev) => applyVatSync([...prev, newItem], vatEnabled, vatRate));
  };

  const goodsItems = items.filter(
    (it) => !(it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat"))))
  );
  const vatItem = items.find(
    (it) => it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat")))
  );

  const totalOrdered = goodsItems.reduce((s, it) => s + (it.quantity || 0), 0);
  const totalDelivered = goodsItems.reduce((s, it) => s + (it.deliveredQty || 0), 0);
  const goodsAmount = goodsItems.reduce((s, it) => s + (it.amount || 0), 0);
  const vatAmount = vatItem ? Number(vatItem.amount || 0) : 0;
  const totalAmount = goodsAmount + vatAmount;

  return (
    <div className="space-y-4 w-full mx-auto pb-8">
      {/* Immutable Document Guard Banner */}
      {isLocked && (
        <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg flex items-center gap-3 text-amber-950 text-xs shadow-xs">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
          <div>
            <span className="uppercase tracking-wide font-black text-amber-900 block flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-amber-700 inline" /> Chứng Từ Đã Khóa Bất Biến 
            </span>
            <p className="text-amber-800 mt-0.5 font-normal">
              Phiếu giao hàng này đã ở trạng thái <strong>{defaultValues?.status === "COMPLETED" ? "HOÀN THÀNH" : "ĐÃ GIAO"}</strong> và đã hạch toán Sổ cái công nợ / Thẻ kho. Toàn bộ thông tin được bảo vệ bất biến, không thể sửa đổi hoặc xóa dòng.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleFormSubmit} className="space-y-4">
        <fieldset disabled={isLocked} className="space-y-4 contents">
        {/* ═══ 1. THÔNG TIN KHÁCH HÀNG & ĐƠN HÀNG ═══ */}
        <Card className="border-none shadow-sm ring-1 ring-slate-200">
          <CardHeader className="bg-slate-50/50 py-3 border-b">
            <CardTitle className="text-sm font-bold flex items-center justify-between">
              <span className="flex items-center gap-2">
                <User className="h-4 w-4 text-indigo-600" /> Chọn Khách hàng & Đơn hàng
              </span>
              {isNew && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFqcModal(true)}
                  className="h-7 text-xs border-emerald-300 text-emerald-700 bg-emerald-50/50 hover:bg-emerald-100 hover:text-emerald-800 font-medium"
                >
                  <PackageCheck className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Chọn từ Lô FQC Đạt
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-1">
                <F
                  form={form}
                  name="deliveryId"
                  label="Mã Phiếu Giao"
                  placeholder="Đang tạo..."
                />
              </div>

              <div className="col-span-1" id="customerId" data-field="customerId">
                <form.Field name="customerId">
                  {(field: any) => (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        Khách hàng <span className="text-destructive">*</span>
                      </Label>
                      <SharedCombobox
                        id="customerId"
                        dataField="customerId"
                        required={true}
                        error={!field.state.value}
                        type="customer"
                        value={field.state.value || ""}
                        valueLabel={(defaultValues as any)?.customer?.shortName || (defaultValues as any)?.customer?.name}
                        onChange={(id, opt) => {
                          field.handleChange(id);
                          form.setFieldValue("orderId", "");
                          setItems([]); // Clear items when customer changes

                          // optionally auto-fill receiver info
                          if (opt?.original) {
                            const c = opt.original;
                            if (
                              !form.getFieldValue("receiverName") &&
                              c.contactName
                            )
                              form.setFieldValue("receiverName", c.contactName);
                            if (!form.getFieldValue("receiverPhone") && c.phone)
                              form.setFieldValue("receiverPhone", c.phone);
                            if (!form.getFieldValue("address") && c.address)
                              form.setFieldValue("address", c.address);
                            if (!form.getFieldValue("salesUserId") && c.salesUsers?.[0]?.userId) {
                              form.setFieldValue("salesUserId", c.salesUsers[0].userId);
                            }
                          }
                        }}
                        placeholder="Chọn KH..."
                      />
                    </div>
                  )}
                </form.Field>
              </div>

              <div className="col-span-1">
                <form.Field name="orderId">
                  {(field: any) => (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">
                          Đơn hàng <span className="text-destructive">*</span>
                        </Label>
                        {selectedCustomerId && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 px-1.5 text-[11px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 font-semibold cursor-pointer"
                            onClick={() => setShowCombineModal(true)}
                            disabled={isLocked}
                          >
                            <Plus className="w-3 h-3 mr-0.5" /> Gộp thêm đơn
                          </Button>
                        )}
                      </div>
                      <SharedCombobox
                        type="order"
                        params={{ customerId: selectedCustomerId || undefined }}
                        value={field.state.value || ""}
                        valueLabel={(defaultValues as any)?.order?.orderId || defaultValues?.orderId}
                        onChange={(id) => {
                          field.handleChange(id);
                          if (id) loadOrderItems(id);
                        }}
                        placeholder="Chọn đơn hàng..."
                        disabled={!selectedCustomerId}
                      />
                    </div>
                  )}
                </form.Field>
              </div>

              <div className="col-span-1">
                <form.Field name="salesUserId">
                  {(field: any) => (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        NV Sale phụ trách
                      </Label>
                      <SharedCombobox
                        type="user"
                        value={field.state.value || ""}
                        valueLabel={(defaultValues as any)?.salesUser?.fullName || (defaultValues as any)?.salesUser?.name}
                        onChange={(id) => {
                          field.handleChange(id);
                        }}
                        placeholder="Chọn NV Sale..."
                      />
                    </div>
                  )}
                </form.Field>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══ 2. THÔNG TIN GIAO HÀNG & VẬN TẢI ═══ */}
        <Card
          id="delivery-location-section"
          className={`border-none shadow-sm transition-all ${
            highlightLocation
              ? "ring-2 ring-indigo-500 bg-indigo-50/10"
              : "ring-1 ring-slate-200"
          }`}
        >
          <CardHeader className="bg-slate-50/50 py-3 border-b">
            <CardTitle className="text-sm font-bold flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-indigo-600" /> Thông tin Vận tải &
                Địa điểm
              </span>
              {!canEditLocation && (
                <Badge
                  variant="outline"
                  className="text-[11px] font-medium bg-amber-50 text-amber-800 border-amber-200"
                >
                  Chỉ cập nhật vị trí khi Bản nháp / Đã lên lịch
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="col-span-1">
                <F
                  form={form}
                  name="deliveryDate"
                  label="Ngày giao"
                  type="date"
                  required
                />
              </div>

              <div className="col-span-1">
                <form.Field name="deliveryType">
                  {(field: any) => (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        Hình thức giao
                      </Label>
                      <ComboboxConfigMaster
                        categoryCode="DELIVERY_TYPE"
                        value={field.state.value}
                        onChange={field.handleChange}
                        placeholder="Chọn hình thức..."
                      />
                    </div>
                  )}
                </form.Field>
              </div>

              <div className="col-span-1">
                <F
                  form={form}
                  name="driverName"
                  label="Tài xế"
                  placeholder="Tên tài xế..."
                />
              </div>
              <div className="col-span-1">
                <F
                  form={form}
                  name="vehicleNumber"
                  label="Biển số xe"
                  placeholder="BSX..."
                />
              </div>

              <div className="col-span-1 sm:col-span-2 md:col-span-2">
                <F
                  form={form}
                  name="receiverName"
                  label="Người nhận"
                  placeholder="Tên người nhận..."
                />
              </div>
              <div className="col-span-1 sm:col-span-2 md:col-span-2">
                <F
                  form={form}
                  name="receiverPhone"
                  label="SĐT người nhận"
                  placeholder="SĐT..."
                />
              </div>
              <div className="col-span-1 md:col-span-4">
                <F
                  form={form}
                  name="address"
                  label="ĐC nhận hàng"
                  placeholder="Địa chỉ giao cụ thể..."
                  required
                  disabled={!canEditLocation}
                />
              </div>
              <div className="col-span-1 md:col-span-4">
                <F
                  form={form}
                  name="transshipmentAddr"
                  label="ĐC trung chuyển"
                  placeholder="Nếu giao chành..."
                  disabled={!canEditLocation}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══ 2. CHI TIẾT GIAO HÀNG CARD ═══ */}
        <Card id="items" data-field="items" className="border-none shadow-sm ring-1 ring-slate-200 overflow-hidden">
          <CardHeader className="bg-slate-50/50 py-2.5 px-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Package className="h-4 w-4 text-indigo-600" /> Chi tiết giao hàng & Dịch vụ <span className="text-destructive">*</span>
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {/* VAT SERVICE CONTROLLER */}
              <div className="flex items-center gap-2 bg-indigo-50/90 px-2.5 py-1 rounded-md border border-indigo-200/80 shadow-2xs">
                <div className="flex items-center gap-1.5">
                  <Switch
                    id="vat-switch"
                    checked={vatEnabled}
                    onCheckedChange={handleToggleVat}
                    disabled={isLocked}
                    className="data-[state=checked]:bg-indigo-600 scale-90"
                  />
                  <Label htmlFor="vat-switch" className="text-xs font-semibold text-indigo-950 cursor-pointer select-none">
                    ⚡ Kèm Dịch Vụ VAT
                  </Label>
                </div>
                {vatEnabled && (
                  <Select
                    value={String(vatRate)}
                    onValueChange={(v) => handleChangeVatRate(Number(v))}
                    disabled={isLocked}
                  >
                    <SelectTrigger className="h-6 text-[11px] font-bold border-indigo-200 bg-white w-[82px] px-1.5 text-indigo-900 shadow-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8" className="text-xs font-medium">VAT 8% (Chuẩn)</SelectItem>
                      <SelectItem value="10" className="text-xs font-medium">VAT 10%</SelectItem>
                      <SelectItem value="5" className="text-xs font-medium">VAT 5%</SelectItem>
                      <SelectItem value="0" className="text-xs font-medium">VAT 0% (Miễn)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {vatEnabled && vatAmount > 0 && (
                  <span className="text-[10px] font-mono font-bold text-indigo-700 bg-white/90 px-1.5 py-0.5 rounded border border-indigo-200/60 shadow-2xs">
                    +{formatNumber(vatAmount)}₫
                  </span>
                )}
              </div>

              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={async () => {
                  const newItems = await Promise.all(
                    items.map(async (item) => {
                      if (!item.productId) return item;
                      try {
                        const p = (await trpcClient.products.getById.query({
                          id: item.productId,
                        })) as any;
                        return {
                          ...item,
                          _fgQty: Number(p?.inventory?.quantity) || item._fgQty || 0,
                        };
                      } catch (e) {
                        return item;
                      }
                    }),
                  );
                  setItems(applyVatSync(newItems, vatEnabled, vatRate));
                  toast.success("Đã đối soát tồn kho WMS mới nhất");
                }}
                className="h-7 text-xs text-indigo-700 bg-indigo-50/60 hover:bg-indigo-100 border-indigo-200"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Tồn kho
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowCombineModal(true)}
                className="h-7 text-xs text-blue-700 bg-blue-50/60 hover:bg-blue-100 border-blue-200 font-semibold"
                disabled={!selectedCustomerId || isLocked}
              >
                <Layers className="h-3.5 w-3.5 mr-1 text-blue-600" /> + Gộp Đơn Hàng
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={addEmptyItem}
                className="h-7 text-xs text-indigo-600 hover:bg-indigo-50"
                disabled={isLocked}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> + Thêm SP
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-purple-700 bg-purple-50/60 hover:bg-purple-100 border-purple-200"
                    disabled={isLocked}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> + Thêm Phụ Phí Dịch Vụ
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="text-xs text-slate-500 font-medium">
                    Chọn loại phụ phí dịch vụ ngoài đơn
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {effectiveServiceTypes.map((st) => (
                    <DropdownMenuItem
                      key={st.value}
                      onClick={() => addServiceItem(st.value)}
                      className="text-xs cursor-pointer flex flex-col items-start gap-0.5"
                    >
                      <span className="font-semibold text-slate-800">{st.label}</span>
                      <span className="text-[10px] text-slate-500">{st.defaultName}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto min-h-[220px]">
              <Table className="min-w-[1150px]">
                <TableHeader>
                  <TableRow className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-700 border-b">
                    <TableHead className="w-10 text-center">STT</TableHead>
                    <TableHead className="min-w-[200px]">Tên SP / Khoản Mục Dịch Vụ</TableHead>
                    <TableHead className="w-16 text-center">ĐVT</TableHead>
                    <TableHead className="w-20 text-right">SL Đặt</TableHead>
                    <TableHead className="w-28 text-right bg-slate-100/40">Thành Phẩm</TableHead>
                    <TableHead className="w-20 text-right">Đã Giao</TableHead>
                    <TableHead className="w-20 text-right text-rose-600 font-bold">Còn Thiếu</TableHead>
                    <TableHead className="w-36 text-right text-indigo-600 font-bold bg-indigo-50/30">SL Giao Đợt Này</TableHead>
                    <TableHead className="w-20 text-right text-amber-600">Bù Hao</TableHead>
                    <TableHead className="w-24 text-right bg-emerald-50/60 text-emerald-800 font-bold">Thực Tính</TableHead>
                    <TableHead className="w-28 text-right">Đơn Giá Net</TableHead>
                    <TableHead className="w-32 text-right">Thành Tiền Net</TableHead>
                    <TableHead className="w-10 text-center"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={13}
                        className="h-36 text-center text-slate-400"
                      >
                        <ArrowDownToLine className="h-8 w-8 mx-auto mb-2 opacity-25" />
                        Vui lòng chọn Đơn hàng hoặc nạp từ KCS để tự động tải danh sách sản phẩm
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, idx) => {
                      const isVatLine = item.isServiceItem && (item.serviceType === "VAT" || (item.productName && item.productName.toLowerCase().includes("vat")));
                      const remaining = item._remainingQty !== undefined
                        ? item._remainingQty
                        : Math.max(0, (item._orderQty || item.quantity || 0) - (item._alreadyDelivered || 0));

                      return (
                        <TableRow
                          key={idx}
                          className={`group hover:bg-slate-50/70 transition-colors border-b ${
                            isVatLine
                              ? "bg-indigo-50/30 border-l-4 border-l-indigo-500"
                              : item.isServiceItem
                              ? "bg-purple-50/20"
                              : ""
                          }`}
                        >
                          <TableCell className="text-center text-[10px] text-slate-400 align-middle">
                            {idx + 1}
                          </TableCell>
                          
                          {/* PRODUCT OR SERVICE NAME WITH EMBEDDED THUMBNAIL */}
                          <TableCell className="align-middle py-2">
                            {isVatLine ? (
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-md bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0 shadow-2xs">
                                  <Sparkles className="w-4 h-4" />
                                </div>
                                <div className="space-y-0.5 min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 border-indigo-200 text-[10px] px-1.5 py-0 font-bold">
                                    DỊCH VỤ VAT ({vatRate}%)
                                  </Badge>
                                  <span className="text-[10px] text-indigo-600 font-medium">Tự động tính từ tổng tiền hàng Net</span>
                                </div>
                                <div className="font-semibold text-xs text-slate-800">
                                  {item.productName}
                                </div>
                                <div className="text-[10px] text-slate-500 italic">
                                  {item.notes}
                                </div>
                              </div>
                              </div>
                            ) : item.isServiceItem ? (
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-md bg-purple-100 text-purple-700 flex items-center justify-center shrink-0 shadow-2xs">
                                  <Receipt className="w-4 h-4" />
                                </div>
                                <div className="space-y-1 min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 border-purple-200 text-[10px] px-1.5 py-0 font-semibold">
                                    PHỤ PHÍ DỊCH VỤ
                                  </Badge>
                                  <Select
                                    value={item.serviceType || "OTHER"}
                                    onValueChange={(v) => {
                                      const matched =
                                        effectiveServiceTypes.find((s) => s.value === v) ||
                                        SERVICE_TYPES.find((s) => s.value === v);
                                      updateItemMultiple(idx, {
                                        serviceType: v,
                                        productName: matched?.defaultName || item.productName,
                                        notes: `Phụ phí dịch vụ (${matched?.label || v})`,
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="h-6 text-[11px] font-medium border-slate-200 w-auto min-w-[130px]">
                                      <SelectValue placeholder="Loại phụ phí" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {effectiveServiceTypes.map((st) => (
                                        <SelectItem key={st.value} value={st.value} className="text-xs">
                                          {st.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <Input
                                  value={item.productName}
                                  onChange={(e) => updateItem(idx, "productName", e.target.value)}
                                  placeholder="Tên khoản mục dịch vụ / phụ phí..."
                                  className="h-7 text-xs font-semibold text-slate-800 bg-purple-50/30 border-purple-200 focus-visible:ring-purple-200"
                                />
                                <Input
                                  value={item.notes || ""}
                                  onChange={(e) => updateItem(idx, "notes", e.target.value)}
                                  placeholder="Ghi chú chi tiết dịch vụ..."
                                  className="h-6 text-[10px] border-none bg-transparent hover:bg-white shadow-none p-0 px-1 focus-visible:ring-0 focus-visible:bg-white"
                                />
                              </div>
                            </div>
                            ) : (
                              <div className="flex items-center gap-2.5">
                                <ProductionThumbnailCell
                                  imageUrl={item.productImage}
                                  layoutUrl={item.printLayoutUrl}
                                  title={item.productName}
                                  size={32}
                                  className="shrink-0"
                                />
                                <div className="space-y-1 min-w-0 flex-1">
                                {!item.productId && !item.productName ? (
                                  <SharedCombobox
                                    type="product"
                                    value={item.productId}
                                    onChange={async (id, opt) => {
                                      if (!id) return;
                                      try {
                                        const p =
                                          opt?.original ||
                                          (await trpcClient.products.getById.query({
                                            id,
                                          }));
                                        if (p) {
                                          updateItemMultiple(idx, {
                                            productId: id,
                                            productName: p.productName || p.name,
                                            unit: p.unit || "Hộp",
                                            unitPrice: p.basePrice || 0,
                                            _fgQty: Number(p.inventory?.quantity) || 0,
                                            productImage: p.image || null,
                                          });
                                        }
                                      } catch (err) {
                                        toast.error("Lỗi tải thông tin sản phẩm");
                                      }
                                    }}
                                    placeholder="Chọn sản phẩm..."
                                    className="h-8 text-xs"
                                  />
                                ) : (
                                  <div className="font-semibold text-xs text-slate-900 whitespace-normal leading-tight">
                                    {item.productName}
                                  </div>
                                )}
                                <Input
                                  value={item.notes}
                                  onChange={(e) =>
                                    updateItem(idx, "notes", e.target.value)
                                  }
                                  placeholder="Ghi chú SP..."
                                  className="h-6 text-[10px] border-none bg-transparent hover:bg-white shadow-none p-0 px-1 focus-visible:ring-0 focus-visible:bg-white text-slate-600"
                                />
                                <div className="flex gap-2">
                                  {item.poNumber && (
                                    <span className="text-[10px] text-slate-500 font-mono">
                                      PO: {item.poNumber}
                                    </span>
                                  )}
                                  {item.soNumber && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 border border-blue-200/60 px-1.5 py-0.5 rounded font-mono font-semibold">
                                      <Layers className="w-2.5 h-2.5 text-blue-500" /> ĐH: {item.soNumber}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            )}
                          </TableCell>

                          {/* UNIT */}
                          <TableCell className="align-top pt-2.5 text-center text-xs text-slate-600">
                            {item.isServiceItem ? (
                              <Input
                                value={item.unit || "Gói"}
                                onChange={(e) => updateItem(idx, "unit", e.target.value)}
                                placeholder="ĐVT"
                                className="h-6 w-14 text-center text-[11px] mx-auto border-slate-200 bg-white p-0"
                              />
                            ) : (
                              item.unit || "Hộp"
                            )}
                          </TableCell>

                          {/* ORDER QTY */}
                          <TableCell className="text-right text-xs font-semibold tabular-nums align-top pt-2.5 text-slate-700">
                            {item.isServiceItem ? (
                              <span className="text-[10px] text-slate-400 italic">—</span>
                            ) : (
                              formatNumber(item._orderQty || item.quantity)
                            )}
                          </TableCell>

                          {/* FINISHED GOODS (KCS + WMS) */}
                          <TableCell className="text-right text-xs tabular-nums align-top pt-2 bg-slate-50/50">
                            {item.isServiceItem ? (
                              <span className="text-[10px] text-slate-400 italic">—</span>
                            ) : (
                              <div className="space-y-0.5">
                                <div className="text-[11px] text-blue-700 font-semibold flex items-center justify-end gap-1" title="Số lượng KCS FQC Đạt">
                                  <span>KCS:</span>
                                  <span className="font-bold">{formatNumber(item._qcPassedQty || 0)}</span>
                                </div>
                                <div className="text-[10px] text-slate-600 flex items-center justify-end gap-1" title="Tồn kho thực tế WMS">
                                  <span>Kho:</span>
                                  <span className="font-medium">{formatNumber(item._fgStockQty || item._fgQty || 0)}</span>
                                </div>
                              </div>
                            )}
                          </TableCell>

                          {/* ALREADY DELIVERED */}
                          <TableCell className="text-right text-xs tabular-nums align-top pt-2.5 text-slate-500">
                            {item.isServiceItem ? (
                              <span className="text-[10px] text-slate-400 italic">—</span>
                            ) : (
                              formatNumber(item._alreadyDelivered || 0)
                            )}
                          </TableCell>

                          {/* SHORTAGE / REMAINING */}
                          <TableCell className="text-right text-xs tabular-nums align-top pt-2.5 font-bold text-rose-600">
                            {item.isServiceItem ? (
                              <span className="text-[10px] text-slate-400 italic">—</span>
                            ) : (
                              formatNumber(remaining)
                            )}
                          </TableCell>

                          {/* DELIVERED THIS BATCH (INPUT + SMART SUGGESTION) */}
                          <TableCell className="align-top pt-1.5 bg-indigo-50/20">
                            {isVatLine ? (
                              <div className="text-right text-xs font-mono font-bold text-indigo-700 pt-2 pr-1">
                                1 gói
                              </div>
                            ) : item.isServiceItem ? (
                              <Input
                                type="text"
                                value={item.deliveredQty === 0 ? "" : formatNumber(item.deliveredQty)}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/\D/g, "");
                                  updateItem(idx, "deliveredQty", raw ? Number(raw) : 0);
                                }}
                                className="h-7 w-20 text-right text-xs font-bold text-purple-700 bg-white ml-auto border-purple-200"
                              />
                            ) : (
                              <div className="space-y-1">
                                <Input
                                  type="text"
                                  value={
                                    item.deliveredQty === 0
                                      ? ""
                                      : formatNumber(item.deliveredQty)
                                  }
                                  onChange={(e) => {
                                    const raw = e.target.value.replace(/\D/g, "");
                                    let val = raw ? Number(raw) : 0;
                                    updateItem(idx, "deliveredQty", val);
                                  }}
                                  className="h-7 w-full text-right text-xs font-bold text-indigo-700 bg-white focus-visible:ring-indigo-300 border-indigo-200"
                                />
                                {item._suggestedQty !== undefined && item._suggestedQty > 0 && item.deliveredQty !== item._suggestedQty && (
                                  <div className="flex justify-end">
                                    <button
                                      type="button"
                                      onClick={() => updateItem(idx, "deliveredQty", item._suggestedQty)}
                                      className="text-[9px] font-semibold text-indigo-600 bg-indigo-100/70 hover:bg-indigo-200 px-1 py-0.5 rounded cursor-pointer transition-colors"
                                      title="Nhấn để áp dụng số lượng gợi ý xuất giao"
                                    >
                                      Gợi ý: {formatNumber(item._suggestedQty)}
                                    </button>
                                  </div>
                                )}
                                {item._orderQty !== undefined &&
                                item.deliveredQty + (item._alreadyDelivered || 0) >
                                  item._orderQty * 1.05 ? (
                                  <div className="text-[9px] text-rose-500 font-medium leading-tight flex items-start justify-end gap-1">
                                    <Info className="h-2.5 w-2.5 inline flex-shrink-0 mt-0.5" />
                                    Vượt &gt;5% ({formatNumber(Math.floor(item._orderQty * 1.05))})
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </TableCell>

                          {/* LOSS QTY */}
                          <TableCell className="align-top pt-1.5">
                            {item.isServiceItem ? (
                              <div className="text-center text-slate-400 pt-2 text-xs">—</div>
                            ) : (
                              <Input
                                type="text"
                                value={
                                  item.lossQty === 0
                                    ? ""
                                    : formatNumber(item.lossQty)
                                }
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/\D/g, "");
                                  updateItem(idx, "lossQty", raw ? Number(raw) : 0);
                                }}
                                className="h-7 w-full text-right text-xs text-amber-700 bg-white focus-visible:ring-amber-200 border-slate-200"
                              />
                            )}
                          </TableCell>

                          {/* COUNTED QTY */}
                          <TableCell className="bg-emerald-50/40 align-top pt-2 text-right">
                            <div className="text-xs font-bold text-emerald-800 tabular-nums">
                              {formatNumber(item.countedQty)}
                            </div>
                          </TableCell>

                          {/* UNIT PRICE (NET) */}
                          <TableCell className="align-top pt-1.5 text-right">
                            {isVatLine ? (
                              <div className="text-xs font-mono font-bold text-indigo-700 pt-1">
                                {formatNumber(item.unitPrice)} ₫
                              </div>
                            ) : item.isServiceItem ? (
                              <Input
                                type="text"
                                value={item.unitPrice === 0 ? "" : formatNumber(item.unitPrice)}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/\D/g, "");
                                  updateItem(idx, "unitPrice", raw ? Number(raw) : 0);
                                }}
                                placeholder="0"
                                className="h-7 w-24 text-right text-xs bg-white font-medium border-slate-200 ml-auto"
                              />
                            ) : (
                              <div className="pt-1 text-xs tabular-nums text-slate-700 font-medium">
                                {formatNumber(item.unitPrice)} <span className="text-[10px] text-slate-400">₫</span>
                              </div>
                            )}
                          </TableCell>

                          {/* AMOUNT (NET) */}
                          <TableCell className="align-top pt-2 text-right tabular-nums font-bold text-slate-900 text-xs">
                            {formatNumber(item.amount)} <span className="text-[10px] font-normal text-slate-400">₫</span>
                          </TableCell>

                          {/* ACTION REMOVE */}
                          <TableCell className="align-top pt-1.5 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-rose-500 hover:bg-rose-50"
                              onClick={() => removeItem(idx)}
                              title={isVatLine ? "Tắt Dịch Vụ VAT" : "Xóa dòng"}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* ═══ FOOTER FINANCIAL SUMMARY ═══ */}
            <div className="bg-slate-50/90 border-t p-3 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex flex-wrap gap-5 text-xs text-slate-600 font-medium">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-slate-400" />
                  Tổng SL Đặt:{" "}
                  <span className="text-slate-900 font-bold">
                    {formatNumber(totalOrdered)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-indigo-500" />
                  Tổng Thực Giao:{" "}
                  <span className="text-indigo-700 font-bold">
                    {formatNumber(totalDelivered)}
                  </span>
                </div>
                {totalOrdered > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400">|</span>
                    <span>Tỷ lệ giao:</span>
                    <span className="font-bold text-emerald-700">
                      {Math.round((totalDelivered / totalOrdered) * 100)}%
                    </span>
                  </div>
                )}
              </div>

              {/* MULTI-TIER FINANCIAL RECONCILIATION */}
              <div className="flex flex-col items-end gap-1.5 min-w-[280px]">
                {/* 1. Tiền hàng Net */}
                <div className="flex items-center justify-between w-full text-xs">
                  <span className="text-slate-500 font-medium">Tiền hàng Net (chưa VAT):</span>
                  <span className="font-semibold text-slate-800 tabular-nums">
                    {formatNumber(goodsAmount)} ₫
                  </span>
                </div>

                {/* 2. Dịch vụ VAT (nếu bật) */}
                {vatEnabled && (
                  <div className="flex items-center justify-between w-full text-xs">
                    <span className="text-indigo-700 font-semibold flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-indigo-500" /> Thuế GTGT (VAT {vatRate}%):
                    </span>
                    <span className="font-bold text-indigo-700 tabular-nums">
                      +{formatNumber(vatAmount)} ₫
                    </span>
                  </div>
                )}

                {/* 3. Phí giao hàng */}
                <form.Field name="deliveryFee">
                  {(field: any) => (
                    <div className="flex items-center justify-between w-full text-xs">
                      <span
                        className="text-slate-500 font-medium cursor-pointer hover:text-slate-800"
                        onClick={() => document.getElementById("shipfee")?.focus()}
                      >
                        Phí vận chuyển / bốc xếp:
                      </span>
                      <div className="w-[110px] relative">
                        <Input
                          id="shipfee"
                          type="text"
                          value={
                            field.state.value === 0
                              ? ""
                              : formatNumber(field.state.value)
                          }
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            field.handleChange(raw ? Number(raw) : 0);
                          }}
                          className="h-6 text-right pr-5 text-xs font-semibold focus-visible:ring-indigo-200"
                          placeholder="0"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
                          ₫
                        </span>
                      </div>
                    </div>
                  )}
                </form.Field>

                {/* 4. Tổng cộng thanh toán */}
                <div className="flex items-center justify-between w-full pt-1.5 border-t border-slate-300/80 mt-0.5">
                  <span className="text-xs font-bold text-slate-900 uppercase tracking-tight">
                    TỔNG CỘNG THANH TOÁN:
                  </span>
                  <form.Subscribe selector={(s: any) => s.values.deliveryFee}>
                    {(deliveryFee: number) => {
                      const grandTotal = totalAmount + (Number(deliveryFee) || 0);
                      return (
                        <div className="text-right">
                          <span className="text-lg font-black text-emerald-700 tabular-nums">
                            {formatNumber(grandTotal)}{" "}
                            <span className="text-xs font-medium">₫</span>
                          </span>
                        </div>
                      );
                    }}
                  </form.Subscribe>
                </div>

                {/* 5. Đọc tiền bằng chữ */}
                <form.Subscribe selector={(s: any) => s.values.deliveryFee}>
                  {(deliveryFee: number) => {
                    const grandTotal = totalAmount + (Number(deliveryFee) || 0);
                    return grandTotal > 0 ? (
                      <div className="text-[10px] text-slate-500 italic text-right max-w-[320px] leading-tight mt-0.5">
                        (Bằng chữ: {numberToVietnameseWords(grandTotal)})
                      </div>
                    ) : null;
                  }}
                </form.Subscribe>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══ 3. GHI CHÚ & COMMENTS ═══ */}
        <Card className="border-none shadow-sm ring-1 ring-slate-200 overflow-hidden">
          <CardHeader className="bg-slate-50/50 py-3 border-b flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Info className="h-4 w-4 text-indigo-600" /> Ghi chú & Comments
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <form.Field name="notes">
              {(field: any) => (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-600">
                      Ghi chú (chọn nhanh)
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "Giao đủ số lượng",
                        "Khách tự nhận tại kho",
                        "Giao kèm mẫu đối",
                        "Hàng giao gấp",
                        "Giao chành xe",
                      ].map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="cursor-pointer hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 text-xs font-normal text-slate-600 py-1 px-2"
                          onClick={() => {
                            const current = field.state.value || "";
                            const appendStr = current
                              ? current.trim().endsWith(";")
                                ? ` ${tag}`
                                : `; ${tag}`
                              : tag;
                            field.handleChange(current + appendStr);
                          }}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-600">
                      Hộp comments
                    </Label>
                    <Textarea
                      value={field.state.value ?? ""}
                      placeholder="Nhập comments hoặc ghi chú chi tiết..."
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="min-h-[80px] text-sm"
                    />
                  </div>
                </div>
              )}
            </form.Field>
          </CardContent>
        </Card>
        </fieldset>

        {/* ═══ ACTIONS ═══ */}
        <div className="sticky bottom-0 z-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 p-4 sm:p-6 bg-white/95 backdrop-blur-md border-t border-slate-200 mt-6 flex justify-start gap-3 shadow-[0_-4px_6px_-1px_rgb(0,0,0,0.05)]">
          <form.Subscribe selector={(s: any) => [s.canSubmit, s.isSubmitting]}>
            {([canSubmit, submitting]: any) => !isLocked ? (
              <SaveActionButtons
                status={currentStatus}
                busy={Boolean(submitting || isSubmitting)}
                onSaveDraft={async () => {
                  saveModeRef.current = "DRAFT";
                  try {
                    await form.handleSubmit();
                  } finally {
                    saveModeRef.current = "SUBMIT";
                  }
                }}
                submitLabel={submitLabel}
              />
            ) : (
              <Button
                type="submit"
                disabled={submitting || isSubmitting || isLocked}
                className={`min-w-[120px] ${isLocked ? "bg-slate-400 cursor-not-allowed text-white" : "bg-indigo-600 hover:bg-indigo-700"}`}
              >
                {isLocked ? (
                  <>
                    <Lock className="mr-2 h-4 w-4" /> Đã Khóa Sổ
                  </>
                ) : submitting || isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Đang cập
                    nhật...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" /> {submitLabel}
                  </>
                )}
              </Button>
            )}
          </form.Subscribe>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="min-w-[100px]"
          >
            Huỷ
          </Button>
        </div>
      </form>

      <FQCReadySelectModal
        open={showFqcModal}
        onOpenChange={setShowFqcModal}
        onSelectBatch={handleSelectFqcBatch}
      />

      <CombineOrderModal
        open={showCombineModal}
        onOpenChange={setShowCombineModal}
        customerId={selectedCustomerId}
        customerName={customerLookup.options.find((c: any) => c.value === selectedCustomerId)?.label}
        excludeOrderIds={[form.getFieldValue("orderId")].filter(Boolean)}
        alreadySelectedDetailIds={items.map((it) => it.orderDetailId).filter(Boolean)}
        onCombine={handleCombineOrder}
      />
    </div>
  );
}
